import {
  type AdminReviewListInput,
  DEFAULT_LIMIT,
  normalizedUuidSchema,
} from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useReviewsListQuery } from '@/entities/review';
import { Button, EmptyState, Pagination, tableStyles } from '@/shared/ui';
import { ReviewListFilters } from './review-list-filters';
import { ReviewListRow } from './review-list-row';

const COLUMNS = ['상품', '별점', '내용', '사진', '작성자', '상태', '작성일'] as const;

const toInput = (
  productId: string,
  rating: string,
  status: string,
  photo: string,
  page: number,
): AdminReviewListInput => {
  const parsedId = normalizedUuidSchema.safeParse(productId.trim());
  return {
    page,
    limit: DEFAULT_LIMIT,
    ...(parsedId.success ? { productId: parsedId.data } : {}),
    ...(rating ? { rating: Number(rating) } : {}),
    ...(status === 'VISIBLE' || status === 'HIDDEN' ? { status } : {}),
    ...(photo ? { hasPhoto: photo === 'true' } : {}),
  };
};

/**
 * 어드민 후기 목록 — 필터·흰 표 카드·페이지 이동(0건이면 숨김). 필터를 바꾸면 첫 쪽으로 돌아간다. 작성자 이름은 서버가 가려서 준다.
 * 상세는 미리 부르지 않는다(열 때마다 조회 기록이 남는다).
 */
export const ReviewList = (): ReactElement => {
  const [productId, setProductId] = useState('');
  const [rating, setRating] = useState('');
  const [status, setStatus] = useState('');
  const [photo, setPhoto] = useState('');
  const [page, setPage] = useState(1);
  const query = useReviewsListQuery(toInput(productId, rating, status, photo, page));
  const data = query.data;
  const idEntered = productId.trim().length > 0;
  const productIdInvalid = idEntered && !normalizedUuidSchema.safeParse(productId.trim()).success;
  const filterChange =
    (set: (value: string) => void) =>
    (value: string): void => {
      set(value);
      setPage(1);
    };
  // 후기는 있는데 보던 쪽이 범위를 벗어나 비었으면(숨김·삭제로 건수가 줄어든 경우) "후기 없음"이 아니라 범위 밖이다.
  const outOfRange =
    data !== undefined &&
    data.items.length === 0 &&
    data.meta.totalPages > 0 &&
    data.meta.page > data.meta.totalPages;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <ReviewListFilters
          productId={productId}
          rating={rating}
          status={status}
          photo={photo}
          productIdInvalid={productIdInvalid}
          onProductIdChange={filterChange(setProductId)}
          onRatingChange={filterChange(setRating)}
          onStatusChange={filterChange(setStatus)}
          onPhotoChange={filterChange(setPhoto)}
        />
        {query.isFetching && data ? (
          <p role="status" className="ml-auto text-sm text-muted-foreground">
            불러오는 중…
          </p>
        ) : null}
      </div>

      <div className="mt-5">
        {data === undefined ? (
          query.isError ? (
            <EmptyState isError>목록을 불러오지 못했습니다: {query.error.message}</EmptyState>
          ) : (
            <EmptyState>불러오는 중…</EmptyState>
          )
        ) : outOfRange ? (
          <div className="flex flex-col items-center gap-4">
            <EmptyState>페이지 범위를 벗어났습니다.</EmptyState>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPage(data.meta.totalPages)}
            >
              마지막 페이지로
            </Button>
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState>조건에 맞는 후기가 없습니다.</EmptyState>
        ) : (
          <div className={tableStyles.card}>
            <table className={tableStyles.table}>
              <thead className={tableStyles.head}>
                <tr>
                  {COLUMNS.map((label) => (
                    <th key={label} className={tableStyles.th}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((review) => (
                  <ReviewListRow key={review.id} review={review} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data !== undefined && !outOfRange ? (
        <Pagination
          className="mt-5 justify-center"
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          onPageChange={setPage}
        />
      ) : null}
    </>
  );
};
